import { useCallback, useState, useSyncExternalStore } from 'react';
import { hasSubscriptionLimit } from '@gonfalon/billing/utils';
import { Button } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import { type Scopes } from '../../auth';
import { Alert, BooleanTag, DefaultCallout, Module, ModuleContent, ModuleHeader, Table } from '../../shared';
import { Box, CircularIconButton, Flex } from '../../shared/components';
import { type Account, MetricType, type Subscription } from '../model';

// setTimeout stores its delay in a signed 32-bit int; anything larger overflows
// and fires immediately. Impersonation windows are normally hours, but clamping
// and re-arming stops a long window from reporting a premature expiry.
const MAX_TIMEOUT_MS = 2_147_483_647;

/**
 * Reports whether `until` is still in the future, flipping to false the moment
 * it passes.
 *
 * No store emits an event when wall-clock time crosses a timestamp, so a plain
 * `Date.now()` comparison during render goes stale: the row keeps claiming
 * enhanced support is on until something unrelated happens to re-render.
 */
type DeadlineEntry = {
  active: boolean;
  listeners: Set<() => void>;
  timer?: ReturnType<typeof setTimeout>;
};

const deadlines = new Map<number, DeadlineEntry>();

function ensureDeadline(until: number) {
  const existing = deadlines.get(until);
  if (existing) {
    return existing;
  }
  const entry: DeadlineEntry = { active: until > Date.now(), listeners: new Set() };
  deadlines.set(until, entry);
  if (!entry.active) {
    return entry;
  }
  const arm = () => {
    const msRemaining = until - Date.now();
    if (msRemaining <= 0) {
      entry.active = false;
      entry.listeners.forEach((listener) => listener());
      return;
    }
    entry.timer = setTimeout(arm, Math.min(msRemaining, MAX_TIMEOUT_MS));
  };
  entry.timer = setTimeout(arm, Math.min(until - Date.now(), MAX_TIMEOUT_MS));
  return entry;
}

function subscribeDeadline(until: number | undefined, listener: () => void) {
  if (until === undefined) {
    return () => {};
  }
  const entry = ensureDeadline(until);
  entry.listeners.add(listener);
  return () => {
    entry.listeners.delete(listener);
    if (entry.listeners.size === 0) {
      if (entry.timer !== undefined) {
        clearTimeout(entry.timer);
      }
      deadlines.delete(until);
    }
  };
}

function readDeadline(until: number | undefined) {
  if (until === undefined) {
    return false;
  }
  return ensureDeadline(until).active;
}

function useIsActiveUntil(until?: number) {
  const subscribe = useCallback((listener: () => void) => subscribeDeadline(until, listener), [until]);
  const getSnapshot = useCallback(() => readDeadline(until), [until]);
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

type AccountSecurityPanelProps = {
  account: Account;
  subscription: Subscription;
  scopes: Scopes;
  setSsoDisabled: () => void;
  isDisablingSso?: boolean;
};

export const AccountSecurityPanel = ({
  account,
  subscription,
  scopes,
  setSsoDisabled,
  isDisablingSso = false,
}: AccountSecurityPanelProps) => {
  const [alertIsOpen, setAlertIsOpen] = useState(false);

  const { samlConfig, enableAccountImpersonationUntil } = account;
  const samlEnabled = samlConfig ? samlConfig.enabled : false;
  const samlRequired = samlConfig ? samlConfig.requireSso : false;

  const isEnhancedSupportEnabled = useIsActiveUntil(enableAccountImpersonationUntil);
  const hasVegaAI = hasSubscriptionLimit(subscription._limits[MetricType.VEGA_AI] ?? null);

  // Gate on requireSso, not enabled. The disable endpoint deliberately clears
  // only requireSso and leaves enabled: true so the account stays in SSO
  // test-drive mode (internal/private/accounts.go postSsoDisable, added in
  // #34308 so accounts with SCIM configured can turn SSO back on). requireSso
  // is also the endpoint's own precondition, so once it is false another click
  // would be a silent no-op that still reports success.
  const canDisableSso = scopes.disableSso && samlRequired;

  const handleDisableSsoConfirm = () => {
    setAlertIsOpen(false);
    setSsoDisabled();
  };

  return (
    <Flex>
      <Box>
        <Module>
          <ModuleHeader>
            <span>
              Security Settings{' '}
              <CircularIconButton
                iconName="info"
                size="small"
                tooltip="If enabled, but not required, SSO is in test-drive mode. Team members can log in from LaunchDarkly OR their identity provider."
                tooltipPlacement="top"
              />
            </span>
          </ModuleHeader>
          <ModuleContent snug>
            <Table className="ssoSettings-Table">
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>MFA Required?</td>
                  <td>
                    <BooleanTag value={account.requireMfa} />
                  </td>
                </tr>
                <tr>
                  <td>SAML SSO Enabled?</td>
                  <td>
                    <BooleanTag value={samlEnabled} />
                    {canDisableSso && (
                      <>
                        <Button
                          className="ssoDisable-Button"
                          variant="destructive"
                          size="small"
                          isDisabled={isDisablingSso}
                          onClick={() => setAlertIsOpen(true)}
                        >
                          <Icon name="cancel" slot="start-icon" />
                          Disable SSO
                        </Button>
                        <Alert
                          intent="danger"
                          isOpen={alertIsOpen}
                          onClose={() => setAlertIsOpen(false)}
                          onCancel={() => setAlertIsOpen(false)}
                          onConfirm={handleDisableSsoConfirm}
                          confirmButtonText="Disable"
                          cancelButtonText="Cancel"
                          title="Disable single sign-on?"
                        >
                          <p>All team members will be required to sign in via their email and password.</p>
                        </Alert>
                      </>
                    )}
                  </td>
                </tr>
                <tr>
                  <td>SAML SSO Required?</td>
                  <td>
                    <BooleanTag value={samlRequired} />
                  </td>
                </tr>
                <tr>
                  <td>SAML SSO Sign on URL</td>
                  <td>
                    {samlConfig && samlConfig.ssoUrl ? (
                      samlConfig.ssoUrl
                    ) : (
                      <DefaultCallout>There is no SAML Configuration for this account.</DefaultCallout>
                    )}
                  </td>
                </tr>
                <tr>
                  <td>Support Generative AI Enabled?</td>
                  <td>
                    <BooleanTag value={account.isSupportGenAiEnabled} />
                  </td>
                </tr>
                <tr>
                  <td>Vega AI Entitled?</td>
                  <td>
                    <BooleanTag value={hasVegaAI} />
                  </td>
                </tr>
                <tr>
                  <td>Enhanced Support Enabled?</td>
                  <td>
                    <BooleanTag value={isEnhancedSupportEnabled} />
                    {isEnhancedSupportEnabled && enableAccountImpersonationUntil && (
                      <span style={{ marginLeft: '8px' }}>
                        until {new Date(enableAccountImpersonationUntil).toLocaleString()}
                      </span>
                    )}
                  </td>
                </tr>
                <tr>
                  <td>Session IP Allowlist Enabled?</td>
                  <td>
                    <BooleanTag value={!!account.sessionIpAllowlistEnabled} />
                  </td>
                </tr>
                <tr>
                  <td>Scoped IP Allowlist Enabled?</td>
                  <td>
                    <BooleanTag value={!!account.apiTokenIpAllowlistEnabled} />
                  </td>
                </tr>
              </tbody>
            </Table>
          </ModuleContent>
        </Module>
      </Box>
    </Flex>
  );
};
